const PREFIX = 'rehearsal-director:draft-v1:';
const MAX_CHARACTERS = 8 * 1024 * 1024;
// In-memory recovery survives route changes even when device storage is denied.
const memory = new Map();
const observed = new Map();
const keyFor = scope => PREFIX + scope;

function decode(raw) {
  if (raw == null) return null;
  if (raw.length > MAX_CHARACTERS) throw new Error('Draft exceeds its storage limit.');
  const record = JSON.parse(raw);
  if (record?.version !== 1 || !record.value || typeof record.value !== 'object' || Array.isArray(record.value)) throw new Error('Draft cannot be read.');
  return record.value;
}

export function readLocalDraft(scope) {
  if (memory.has(scope)) return structuredClone(memory.get(scope));
  try { const raw = globalThis.localStorage.getItem(keyFor(scope)); observed.set(scope, raw); return decode(raw); }
  catch { return null; }
}

export function saveLocalDraft(scope, value) {
  memory.set(scope, structuredClone(value));
  try {
    // Never replace a damaged or future-version draft with an empty fallback.
    const previous = globalThis.localStorage.getItem(keyFor(scope));
    if (observed.has(scope) && previous !== observed.get(scope)) return 'Another tab changed this draft. Your current work is kept in this tab. Copy it or save it as a separate script before reopening the latest device draft.';
    decode(previous);
    const raw = JSON.stringify({ version: 1, value });
    if (raw.length > MAX_CHARACTERS) throw new Error('Draft exceeds its storage limit.');
    globalThis.localStorage.setItem(keyFor(scope), raw);
    observed.set(scope, raw);
    memory.delete(scope);
    return '';
  } catch {
    return 'Draft kept for this visit, but it could not be saved on this device. Keep this tab open, copy your text or save your script, and retry after checking device storage.';
  }
}

export function discardLocalDraft(scope) {
  // A failed removal must not claim the stored draft has been discarded.
  const current = globalThis.localStorage.getItem(keyFor(scope));
  if (current !== null && observed.has(scope) && current !== observed.get(scope)) throw new Error('Another tab changed this draft. Copy your current work, then reload to use the latest draft before discarding it.');
  globalThis.localStorage.removeItem(keyFor(scope));
  memory.delete(scope);
  observed.delete(scope);
}

// Validate editable shape without normalization: invalid lengths and broken
// references must stay intact so the person can repair them without data loss.
export function isImportDraft(value) {
  const p = value?.parsed;
  return Boolean(value && typeof value.title === 'string' && typeof value.source === 'string' && (!p || typeof p.error === 'string' && p.error.length > 0 ||
    (Array.isArray(p.characters) && p.characters.every(c => c && typeof c.id === 'string' && typeof c.name === 'string') &&
     Array.isArray(p.scenes) && p.scenes.every(s => s && typeof s.id === 'string' && typeof s.heading === 'string') &&
     Array.isArray(p.entries) && p.entries.every(e => e && typeof e.type === 'string' && typeof e.text === 'string'))));
}

export function isEditorDraft(value, id) {
  const d = value?.draft;
  const optionalText = (item, keys) => keys.every(k => item[k] == null || typeof item[k] === 'string');
  return Boolean(d && d.id === id && typeof value.base === 'string' &&
    Array.isArray(d.characters) && d.characters.every(c => c && typeof c.id === 'string' && typeof c.name === 'string') &&
    Array.isArray(d.scenes) && d.scenes.every(s => s && typeof s.id === 'string' && typeof s.title === 'string') &&
    Array.isArray(d.cues) && d.cues.every(c => c && typeof c.id === 'string' && typeof c.text === 'string' && optionalText(c, ['dialogueText', 'directionText', 'label', 'situationUpdateText']) && (!c.characterIds || Array.isArray(c.characterIds) && c.characterIds.every(id => typeof id === 'string'))) &&
    (!d.actorBriefs || Array.isArray(d.actorBriefs) && d.actorBriefs.every(b => b && typeof b.id === 'string' && typeof b.text === 'string')));
}
